import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from "firebase/firestore";
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from "firebase/storage";
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from "firebase/functions";
import { getAnalytics, isSupported, type Analytics } from "firebase/analytics";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  type AppCheck,
} from "firebase/app-check";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
// Functions live in europe-west1 — see /functions/src/*.ts region constants.
export const functions: Functions = getFunctions(app, "europe-west1");

// Analytics : chargement conditionné au consentement (RGPD/CNIL).
// Ne PAS appeler getAnalytics() au top-level — ça déposerait des cookies GA4
// sans consentement. Le module de consentement (src/lib/consent.ts) appelle
// enableAnalyticsWithConsent() une fois l'utilisateur d'accord, et seulement
// dans ce cas.
export let analytics: Analytics | null = null;

export async function enableAnalyticsWithConsent(): Promise<void> {
  if (analytics) return;
  try {
    const ok = await isSupported();
    if (!ok) return;
    analytics = getAnalytics(app);
  } catch {
    // ignore — analytics reste optionnel
  }
}

// Wire emulators when VITE_USE_EMULATORS=1 (local dev only)
const USE_EMULATORS = import.meta.env.VITE_USE_EMULATORS === "1";
if (USE_EMULATORS) {
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectStorageEmulator(storage, "localhost", 9199);
  connectFunctionsEmulator(functions, "localhost", 5001);
}

// App Check — gates callable functions & direct Firestore access against
// abusive automation. Emulator connections bypass App Check automatically,
// so we only init when targeting real backends.
//
// To register the site key:
//   Firebase Console → Project settings → App Check → Register reCAPTCHA v3
//   provider for the web app, copy the public site key into VITE_RECAPTCHA_SITE_KEY.
//
// For local dev against production backends, set a debug token BEFORE init:
//   window.self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;   // then copy from console
export let appCheck: AppCheck | null = null;
const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (!USE_EMULATORS && SITE_KEY) {
  if (import.meta.env.DEV) {
    // Emits a one-time debug token in devtools that you can register under
    // Firebase Console → App Check → Manage debug tokens.
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}
